import { ViewAllButton } from '@components'
import CoreLoader from '@components/CoreLoader'
import type { SerializedError } from '@reduxjs/toolkit'
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query'
import TransactionsTable from '@views/transactions/elements/TransactionsTable'
import { paths, useFeatureFlagState, useGetTransactionsQuery, useTransactionColumns } from 'mmfintech-backend-api'
import type { ErrorObject, TransactionViewPortal } from 'mmfintech-backend-api/types'
import { tr } from 'mmfintech-commons'
import { ErrorDisplay } from 'mmfintech-portal-commons'
import { useEffect } from 'react'

import './transactionsSection.scss'

type TTransactionsSectionProps = {
  title?: string
  transactionsData?: TransactionViewPortal[]
  transactionsDataError?: ErrorObject | FetchBaseQueryError | SerializedError
  transactionsDataFetching?: boolean
  recipientId?: number
}

export const TransactionsSection = (props: TTransactionsSectionProps) => {
  const { title = '', transactionsData, transactionsDataError, transactionsDataFetching, recipientId } = props

  const accountingCategoryFF = useFeatureFlagState('WITHDRAW_ACCOUNTING_CATEGORY')

  const columns = useTransactionColumns({
    cookieDomain: null,
    enableCustomerEmail: false,
    enableFailReason: true,
    enableAccountingCategory: accountingCategoryFF.isEnabled
  })

  useEffect(() => {
    if (columns) {
      columns.setVisible(['id', 'type', 'description', 'status'])
    }
  }, [])

  const {
    data: transactions,
    error: transactionsError,
    isFetching: transactionsFetching
  } = useGetTransactionsQuery({ size: 3, sort: 'reqTime-desc,id-desc' }, { skip: !!transactionsData })

  return (
    <div className='transactions-section-wrapper'>
      <div className='transactions-section-header'>
        <h2 className='transactions-section-title'>
          {title || tr('FRONTEND.DASHBOARD.LATEST_TRANSACTIONS_TITLE', 'Latest transactions')}
        </h2>
        <ViewAllButton
          label={tr('FRONTEND.DASHBOARD.TRANSACTIONS.BUTTON.VIEW_ALL', 'View all')}
          redirect={paths.banking.transactions.list()}
          recipientId={recipientId}
        />
      </div>

      <ErrorDisplay error={[transactionsError, transactionsDataError]} />
      {transactionsFetching || transactionsDataFetching ? (
        <CoreLoader />
      ) : (
        <TransactionsTable transactions={transactions?.content || transactionsData || []} columns={columns} />
      )}
    </div>
  )
}
